import React, { useEffect, useState } from "react";
import { fetch } from "../lib/network";
import { Icon, SemanticICONS } from "semantic-ui-react";
import { useThumbnail } from "../features/permits/hooks/useThumbnail";
import { CarouselImage } from "./ImageCarousel";
import * as fileTypes from "../utils/mimetypes";

export const enum ThumbnailSize {
  Medium = "medium",
  Small = "small",
}

export function Thumbnail({
  image,
  size,
  onClick,
}: {
  image: CarouselImage;
  size: ThumbnailSize;
  onClick?: () => void;
}) {
  const [isValidImage, setIsValidImage] = useState<boolean>(true);
  const { data: thumbnailData, isLoading: isThumbnailLoading } = useThumbnail(
    image.fileId,
    size,
    2000,
    !isValidImage, // Enable fetching when image is invalid
  );

  useEffect(() => {
    const validateImage = async () => {
      const imageUrl = image.thumbnails?.[size] ?? image.url;
      if (!imageUrl) {
        setIsValidImage(false);
        return;
      }

      try {
        const response = await fetch(imageUrl);
        const isValid = response.ok;
        setIsValidImage(isValid);
      } catch (error) {
        setIsValidImage(false);
      }
    };

    setIsValidImage(true); // Reset state before validation
    validateImage();
  }, [image.thumbnails, image.url, size]);

  const isLoading = (!image.url && !thumbnailData?.url) || (!isValidImage && isThumbnailLoading);
  const thumbnailFetchedSuccessfully = !isValidImage && thumbnailData?.data?.status === "Complete" && thumbnailData.url;
  const thumbnailFailedToFetch = !isValidImage && thumbnailData?.data?.status === "Error";

  if (isLoading) {
    return (
      <div className={`ui placeholder segment image-size-${size} image-loading`}>
        <div className='ui active centered inline loader'></div>
      </div>
    );
  }
  if (thumbnailFetchedSuccessfully) {
    return (
      <div role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}>
        <img
          src={thumbnailData.url ?? ""}
          alt='attachment'
          className={`image-size-${size} tw-rounded-lg tw-object-cover`}
          onError={() => setIsValidImage(false)}
        />
        {thumbnailData.fileType && fileTypes.isVideo(thumbnailData.fileType) && <VideoOverlay />}
      </div>
    );
  }
  if (!isValidImage || thumbnailFailedToFetch) {
    return <DefaultThumbnail fileType={image.fileType} size={size} onClick={onClick} />;
  }

  return (
    <div role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}>
      <img
        src={image.thumbnails?.[size] ?? image.url ?? ""}
        alt='attachment'
        className={`image-size-${size} tw-rounded-lg tw-object-cover`}
        onError={() => setIsValidImage(false)}
      />
      {fileTypes.isVideo(image.fileType) && <VideoOverlay />}
    </div>
  );
}

export function DefaultThumbnail({
  fileType,
  size,
  onClick,
}: {
  fileType: string;
  size: ThumbnailSize;
  onClick?: () => void;
}) {
  const typeSettings = (type: string): SemanticICONS => {
    if (fileTypes.isImage(type)) return "file image outline";
    if (fileTypes.isVideo(type)) return "file video outline";
    if (fileTypes.isAudio(type)) return "file audio outline";
    if (fileTypes.isWord(type)) return "file word outline";
    if (fileTypes.isPowerpoint(type)) return "file powerpoint outline";
    if (fileTypes.isExcel(type)) return "file excel outline";
    if (fileTypes.isPDF(type)) return "file pdf outline";
    return "file outline";
  };
  return (
    <div
      className={`tw-m-0 tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-bg-grey-light tw-leading-10 image-size-${size} image-loading`}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
    >
      <Icon name={typeSettings(fileType)} className='tw-m-0 tw-text-4xl tw-leading-10' />
    </div>
  );
}

function VideoOverlay() {
  return (
    <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'>
      <Icon className='tw-m-0 tw-h-8 tw-w-8 tw-text-3xl tw-leading-none tw-text-white' name='play circle outline' />
    </div>
  );
}
